import { useState, useCallback } from 'react'
import { RefreshCw, Download, Eye, ShieldAlert, Star } from 'lucide-react'
import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DataTableBrowser,
  DataCatalogEmpty,
  SensitiveDataReport,
} from '@/components/data-catalog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDataTables,
  useScanTable,
  useDetectSensitiveData,
} from '@/hooks/use-data-tables'
import { useSyncFromDiscovery } from '@/hooks/use-catalog-sync'
import { useScans } from '@/hooks/use-discovery'
import { useConnections } from '@/hooks/use-discovery'
import { useClassificationTree } from '@/hooks/use-classifications'
import type { DataTable, SensitiveDataReport as Report } from '@/types/data-table'

/**
 * Default pagination configuration
 */
const DEFAULT_PAGE_SIZE = 20

/**
 * Data Catalog page component
 * Main page for browsing and discovering data tables and columns
 */
export function DataCatalogPage() {
  // Filter state
  const [search, setSearch] = useState('')
  const [connectionFilter, setConnectionFilter] = useState<string>('all')
  const [classificationFilter, setClassificationFilter] = useState<string>('all')
  const [requiresReviewFilter, setRequiresReviewFilter] = useState(false)
  const [highConfidenceFilter, setHighConfidenceFilter] = useState(false)
  const [hasRegulatoryFlagsFilter, setHasRegulatoryFlagsFilter] = useState(false)
  const [page, setPage] = useState(1)

  // Dialog state
  const [sensitiveDataReport, setSensitiveDataReport] = useState<Report | null>(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedScanId, setSelectedScanId] = useState<string>('')
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('')

  // Fetch data
  const { data: tablesData, isLoading, isError, error, refetch } = useDataTables({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    applicationId: connectionFilter === 'all' ? undefined : connectionFilter,
    classificationId: classificationFilter === 'all' ? undefined : classificationFilter,
    search: search || undefined,
    requiresReview: requiresReviewFilter || undefined,
    minConfidence: highConfidenceFilter ? 0.8 : undefined,
    hasRegulatoryFlags: hasRegulatoryFlagsFilter || undefined,
  })

  // Fetch filter options
  const { data: connectionsData } = useConnections({ pageSize: 100 })
  const { data: classificationTree } = useClassificationTree()

  // Fetch completed scans for import dialog
  const { data: scansData } = useScans({ status: 'COMPLETED', pageSize: 50 })

  // Mutations
  const scanTable = useScanTable()
  const detectSensitive = useDetectSensitiveData()
  const syncFromDiscovery = useSyncFromDiscovery()

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleConnectionFilterChange = useCallback((value: string) => {
    setConnectionFilter(value)
    setPage(1)
  }, [])

  const handleClassificationFilterChange = useCallback((value: string) => {
    setClassificationFilter(value)
    setPage(1)
  }, [])

  const handleScanTable = useCallback(
    (table: DataTable) => {
      scanTable.mutate(table.id)
    },
    [scanTable]
  )

  const handleDetectSensitive = useCallback(
    (table: DataTable) => {
      detectSensitive.mutate(table.id, {
        onSuccess: (report) => {
          setSensitiveDataReport(report)
          setReportDialogOpen(true)
        },
      })
    },
    [detectSensitive]
  )

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handleImportFromScan = useCallback(() => {
    if (!selectedScanId || !selectedConnectionId) return

    syncFromDiscovery.mutate(
      { scanId: selectedScanId, applicationId: selectedConnectionId },
      {
        onSuccess: () => {
          setImportDialogOpen(false)
          setSelectedScanId('')
          setSelectedConnectionId('')
        },
      }
    )
  }, [selectedScanId, selectedConnectionId, syncFromDiscovery])

  const tables = tablesData?.data ?? []
  const pagination = tablesData?.pagination
  const connections = connectionsData?.data ?? []
  const completedScans = scansData?.data ?? []
  const showEmpty =
    !isLoading &&
    tables.length === 0 &&
    !search &&
    connectionFilter === 'all' &&
    classificationFilter === 'all' &&
    !requiresReviewFilter &&
    !highConfidenceFilter &&
    !hasRegulatoryFlagsFilter

  // Flatten classification tree for select options
  const flattenClassifications = (nodes: any[]): any[] => {
    const result: any[] = []
    for (const node of nodes) {
      result.push(node)
      if (node.children && node.children.length > 0) {
        result.push(...flattenClassifications(node.children))
      }
    }
    return result
  }

  const classifications = classificationTree ? flattenClassifications(classificationTree) : []

  return (
    <>
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <h1 className="text-lg font-semibold">Data Catalog</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportDialogOpen(true)}
          >
            <Download className="h-4 w-4 mr-1" />
            Import from Scan
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            <span className="sr-only">Refresh</span>
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4">
        {/* Info Card */}
        <div className="rounded-lg border bg-muted/50 p-4">
          <h2 className="font-semibold mb-2">About Data Catalog</h2>
          <p className="text-sm text-muted-foreground">
            Browse and discover data tables across your applications. Import scan
            results from Data Discovery to populate the catalog, then create
            XACML policies to protect sensitive data.
          </p>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          {/* Search */}
          <Input
            placeholder="Search tables..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />

          {/* Filter selects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Connection filter */}
            <Select
              value={connectionFilter}
              onValueChange={handleConnectionFilterChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Connection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Connections</SelectItem>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Classification filter */}
            <Select
              value={classificationFilter}
              onValueChange={handleClassificationFilterChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Classification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {classifications.map((classification) => (
                  <SelectItem key={classification.id} value={classification.id}>
                    {classification.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quick filter toggles */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={requiresReviewFilter ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setRequiresReviewFilter((prev) => !prev)
                setPage(1)
              }}
            >
              <Eye className="h-4 w-4 mr-1" />
              Needs Review
            </Button>
            <Button
              variant={highConfidenceFilter ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setHighConfidenceFilter((prev) => !prev)
                setPage(1)
              }}
            >
              <Star className="h-4 w-4 mr-1" />
              High Confidence
            </Button>
            <Button
              variant={hasRegulatoryFlagsFilter ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setHasRegulatoryFlagsFilter((prev) => !prev)
                setPage(1)
              }}
            >
              <ShieldAlert className="h-4 w-4 mr-1" />
              Regulatory Flags
            </Button>
          </div>
        </div>

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Error loading data tables:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {showEmpty && <DataCatalogEmpty />}

        {/* Data Tables Browser */}
        {!isLoading && tables.length > 0 && (
          <>
            <DataTableBrowser
              tables={tables}
              onScanTable={handleScanTable}
              onDetectSensitive={handleDetectSensitive}
            />

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {tables.length} of {pagination.totalItems} table
                  {pagination.totalItems === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* No results after filtering */}
        {!isLoading && tables.length === 0 && !showEmpty && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No data tables found matching your filters.
            </p>
          </div>
        )}
      </div>

      {/* Sensitive Data Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sensitive Data Detection Report</DialogTitle>
            <DialogDescription>
              Results of sensitive data pattern detection scan
            </DialogDescription>
          </DialogHeader>
          {sensitiveDataReport && (
            <SensitiveDataReport report={sensitiveDataReport} />
          )}
        </DialogContent>
      </Dialog>

      {/* Import from Scan Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import from Discovery Scan</DialogTitle>
            <DialogDescription>
              Select a completed discovery scan and target application to import
              classified data into the catalog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Completed Scan</Label>
              <Select value={selectedScanId} onValueChange={setSelectedScanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a scan..." />
                </SelectTrigger>
                <SelectContent>
                  {completedScans.map((scan) => (
                    <SelectItem key={scan.id} value={scan.id}>
                      {scan.connection?.name ?? 'Unknown'} -{' '}
                      {new Date(scan.createdAt).toLocaleDateString()} (
                      {scan.columnsClassified} classified)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Connection</Label>
              <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select connection..." />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id}>
                      {conn.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportFromScan}
              disabled={
                !selectedScanId ||
                !selectedConnectionId ||
                syncFromDiscovery.isPending
              }
            >
              {syncFromDiscovery.isPending ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
